import { fetch } from 'owa-fetch';
import { getBusinessLogo } from 'bookings-shared/lib/c2/services/getBusinessLogo';
import { getBusinessInfo } from 'bookings-shared/lib/c2/services/getBusinessInfo';
import { getServices } from 'bookings-shared/lib/c2/services/getServices';
import { listServices } from 'bookings-shared/lib/c2/services/listServices';
import { getAppointment } from 'bookings-shared/lib/c2/services/getAppointment';
import { getCustomQuestions } from 'bookings-shared/lib/c2/services/getCustomQuestions';
import { mapServicesResponse } from '../mappers/modelB2/mapServicesResponse';
import {
    mapBusinessInfoResponse,
    settingsMapper,
} from '../mappers/modelB2/mapBusinessInfoResponse';
import { mapAppointmentResponse } from '../mappers/modelB2/mapAppointmentResponse';
import type { PersonalBookingData } from '../store/schema/PersonalBookingData';
import getLoggedInUserHashedEmail from '../selectors/getLoggedInUserHashedEmail';
import getUserClientId from '../selectors/getUserClientId';
import getBookingMailboxPuid from '../selectors/getBookingMailboxPuid';
import type { Service } from 'bookings-shared/lib/c2/services/schema/service';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import setObscuredBookingSmtpAddress from '../mutators/setObscuredBookingSmtpAddress';
import { hasQueryStringParameter, getQueryStringParameter } from 'owa-querystring';
import setCustomQuestions from '../mutators/setCustomQuestions';
import setUserClientId from '../mutators/setUserClientId';
import { isFeatureEnabled } from 'owa-feature-flags';
import setMailboxPuid from '../mutators/setMailboxPuid';
import { getTenantId } from 'bookwithme-shared/lib/utils/getTenantId';
import setGetAppointmentResponse from '../mutators/setGetAppointmentResponse';
import setBEServerMutator from '../mutators/setBEServerMutator';

/**
 * Orchestrator for pageDataLoad
 */

export const bookingCodeQueryStringParameter = 'bookingcode';
export const getPageDataLoadOrchestrator = async (
    smtpAddress: string,
    bookingServiceId?: string,
    appointmentId?: string
): Promise<PersonalBookingData | null> => {
    const perfDatapoint = new PerformanceDatapoint('pageDataPayload');

    //Wrong Server Exception Bug
    //Calling eventify to get the beServer before making B2 calls
    let beServer;
    try {
        const res = await fetch(`/owa/calendar/${smtpAddress}/pbp/?eventify=2`);
        beServer = res.headers.get('X-Calculatedbetarget') ?? '';
        setBEServerMutator(beServer);
    } catch (error) {
        // Handle the error gracefully
        //Setting beServer as empty in case of undefined
        setBEServerMutator('');
    }

    const obscuredSmtpAddress = await getTenantId(smtpAddress); //obscuredSmtpAddress = {mailboxGuid}@{tenantGuid}
    try {
        const _obscuredSmtpAddress = obscuredSmtpAddress.split('@');

        // Temporary patch for the specific tenant to block C2 page of BWM.
        // Will be removed once backend changes are ready
        if (_obscuredSmtpAddress[1] === '15ef12a1-af58-44c4-b029-712fc0605570') {
            return null;
        }

        const business = await getBusinessInfo(
            {
                smtpAddress: `mbx:${obscuredSmtpAddress}`,
                isAuthRequired: false,
            },
            beServer
        );

        setUserClientId(business?.clientId ?? '');
        setMailboxPuid(business?.mailboxPuid ?? '');

        if (!!business && business.businessInfo?.isPublished) {
            setObscuredBookingSmtpAddress(obscuredSmtpAddress);

            //Business Logo API
            const logo = await getBusinessLogo(
                {
                    smtpAddress: `mbx:${obscuredSmtpAddress}`,
                    isAuthRequired: false,
                },
                beServer
            );

            const businessLogo = logo?.image ?? null;

            //appointment API
            let appointmentResponseB2 = null;
            if (!!appointmentId) {
                const appointment = await getAppointment(
                    {
                        smtpAddress: `mbx:${obscuredSmtpAddress}`,
                        appointmentId,
                        isAuthRequired: false,
                    },
                    beServer
                );
                if (appointment) {
                    setGetAppointmentResponse(appointment);
                    appointmentResponseB2 = mapAppointmentResponse(appointment);
                    bookingServiceId = appointmentResponseB2.ServiceId;
                }
            }

            //Service API
            let serviceResponse: Service[] = [];
            if (bookingServiceId) {
                var queryString = '';
                if (hasQueryStringParameter(bookingCodeQueryStringParameter)) {
                    queryString =
                        `${bookingCodeQueryStringParameter}=` +
                        getQueryStringParameter(bookingCodeQueryStringParameter);
                } else if (appointmentResponseB2 !== null) {
                    queryString = `${bookingCodeQueryStringParameter}=` + appointmentId;
                }
                const services = await getServices(
                    {
                        smtpAddress: `mbx:${obscuredSmtpAddress}`,
                        bookingServiceId,
                        isAuthRequired: false,
                        queryString,
                    },
                    beServer
                );
                if (services?.error) {
                    // since getservices failed, returning null to show link is not valid error
                    return null;
                }
                if (services) {
                    serviceResponse = [services.service];
                }
            } else {
                const services = await listServices(
                    {
                        smtpAddress: `mbx:${obscuredSmtpAddress}`,
                        isAuthRequired: false,
                    },
                    beServer
                );
                if (services) {
                    serviceResponse = services.service;
                }
            }
            let mappedServices = mapServicesResponse(serviceResponse);

            // If bookingId is present, only display the service related to the bookingId
            if (!!appointmentId && appointmentResponseB2?.ServiceId) {
                bookingServiceId = appointmentResponseB2?.ServiceId;
                mappedServices = mappedServices.filter(service => service?.Id === bookingServiceId);
            }
            const isReadRequest = appointmentId ? true : false;

            let displayedServiceNotFound = false;
            if (bookingServiceId && mappedServices.length === 0) {
                displayedServiceNotFound = true;
            }

            if (isFeatureEnabled('bookwithme-customFields')) {
                //Custom Questions API
                const customQuestions = await getCustomQuestions({
                    smtpAddress: `mbx:${obscuredSmtpAddress}`,
                    isAuthRequired: false,
                });
                //mutator for custom questions
                setCustomQuestions(customQuestions?.questions ?? []);
            }

            const mappedResponse: PersonalBookingData = {
                Booking: appointmentResponseB2,
                BookingMailboxAddress: smtpAddress,
                BookingMailboxHash: null, //not present in backend
                BookingMailboxPuid: getBookingMailboxPuid(),
                BusinessInfo: mapBusinessInfoResponse(business),
                BusinessLogo: businessLogo,
                BusinessTimeZone: business.businessInfo.businessTimeZone,
                BusinessTimeZoneName: business.businessInfo.businessTimeZoneName,
                StaffMembers: [
                    { Id: 'h3gcHPzgm0esfWmD0PAm/w==', Name: business.businessInfo.displayName },
                ], //using selfIdEncoded
                Services: mappedServices,
                Settings: settingsMapper(business),
                IsReadRequest: isReadRequest, //Not Available in backend
                DisplayedServiceNotFound: displayedServiceNotFound,
                LoggedInUserHashedEmail: getLoggedInUserHashedEmail(),
                UserClientId: getUserClientId(),
            };
            return mappedResponse;
        }
        return null;
    } catch (ex) {
        perfDatapoint.endWithError(DatapointStatus.ServerError);
        return null;
    }
};

export default getPageDataLoadOrchestrator;
